"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { env } from "@/env"
import { getUser } from "@/redux/features"
import { useAppSelector } from "@/redux/hooks"
import { debug } from "@/utils"
import { io, Socket } from "socket.io-client"

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
})

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const user = useAppSelector(getUser)
  const userId = user?._id || null

  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    let socketInstance: Socket

    const initSocket = () => {
      socketInstance = io(env.NEXT_PUBLIC_SOCKET_URL, {
        withCredentials: true,
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        autoConnect: true,
        timeout: 10000,
        ...(userId ? { query: { userId } } : {}),
      })

      socketInstance.on("connect", () => {
        setIsConnected(true)
      })

      socketInstance.on("disconnect", () => {
        setIsConnected(false)
      })

      socketInstance.on("connect_error", (err) => {
        debug("Connection Error", { level: "error", data: err })
      })

      setSocket(socketInstance)
    }

    if (typeof window !== "undefined") {
      initSocket()
    }

    return () => {
      if (socketInstance) {
        socketInstance.off("connect")
        socketInstance.off("disconnect")
        socketInstance.off("connect_error")
        socketInstance.disconnect()
      }
    }
  }, [])

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider")
  }
  return context
}
